import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CookiePolicy = () => {
  const clearCookieConsent = () => {
    localStorage.removeItem('sanibiz_cookie_consent');
    localStorage.removeItem('sanibiz_cookie_consent_date');
    alert('Cookie preferences cleared. Please refresh the page to see the consent banner again.');
  };

  const cookieTypes = [
    {
      name: 'Strictly Necessary Cookies',
      icon: '🔒',
      color: 'red',
      description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, authentication, and accessibility.',
      examples: [
        'JWT authentication tokens (7-day retention)',
        'Session management cookies',
        'Security and fraud prevention',
        'User authentication state'
      ],
      canDisable: false
    },
    {
      name: 'Functional Cookies',
      icon: '⚙️',
      color: 'blue',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      examples: [
        'Language preferences',
        'Display preferences (theme, layout)',
        'User interface customization',
        'Feature access preferences'
      ],
      canDisable: true
    },
    {
      name: 'Analytics Cookies',
      icon: '📊',
      color: 'green',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: [
        'Page view statistics',
        'Feature usage analytics',
        'Performance monitoring',
        'Error tracking and debugging'
      ],
      canDisable: true
    },
    {
      name: 'Performance Cookies',
      icon: '⚡',
      color: 'yellow',
      description: 'These cookies collect information about how you use our website to help improve its performance and user experience.',
      examples: [
        'Load time measurements',
        'Network performance data',
        'Browser compatibility data',
        'Device type and screen size'
      ],
      canDisable: true
    }
  ];

  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'bg-red-200',
      badge: 'bg-red-100 text-red-700'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'bg-blue-200',
      badge: 'bg-blue-100 text-blue-700'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'bg-green-200',
      badge: 'bg-green-100 text-green-700'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: 'bg-yellow-200',
      badge: 'bg-yellow-100 text-yellow-700'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navbar />

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 relative overflow-hidden mb-8">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 rounded-full -translate-y-20 translate-x-20"></div>
            <div className="relative">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-gradient-to-r from-orange-500 to-yellow-600 text-white p-3 rounded-xl">
                  <span className="text-3xl">🍪</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                    Cookie Policy
                  </h1>
                  <p className="text-gray-600 mt-2">SANIBIZ MIS - Understanding our cookie usage</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 bg-orange-50 px-6 py-3 rounded-lg">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0v12a2 2 0 002 2h4a2 2 0 002-2V7m-6 0V6" />
                  </svg>
                  <span className="text-orange-900 font-semibold">Last Updated:</span>
                </div>
                <span className="text-orange-700 font-medium">November 16, 2025</span>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are stored on your device (computer, smartphone, or tablet) when you visit our website. 
              They help us provide you with a better experience by remembering your preferences, keeping you logged in, and understanding 
              how you use our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              At SANIBIZ MIS, we use cookies to enhance your experience with our tiles and sanitary products management system, 
              maintain secure authentication, and improve our services based on usage patterns.
            </p>
          </div>

          {/* Cookie Types */}
          <div className="space-y-6 mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Types of Cookies We Use</h2>
            
            {cookieTypes.map((cookie, index) => {
              const colors = colorClasses[cookie.color];
              return (
                <div key={index} className={`${colors.bg} rounded-2xl p-6 border ${colors.border} shadow-lg`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className={`${colors.icon} p-3 rounded-lg`}>
                        <span className="text-2xl">{cookie.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{cookie.name}</h3>
                        {!cookie.canDisable && (
                          <span className={`inline-block ${colors.badge} text-xs font-semibold px-3 py-1 rounded-full mt-1`}>
                            Always Active
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">{cookie.description}</p>
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Examples:</h4>
                    <ul className="space-y-2">
                      {cookie.examples.map((example, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                          <span className="text-gray-700 text-sm">{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Managing Cookies */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 shadow-lg border border-purple-200 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">🔧 Browser Settings</h3>
                <p className="text-gray-700 text-sm">
                  You can control and delete cookies through your browser settings. However, please note that blocking 
                  certain cookies may impact your experience and prevent some features from working properly.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">🔄 Reset Preferences</h3>
                <p className="text-gray-700 text-sm mb-4">
                  You can reset your cookie preferences for SANIBIZ MIS at any time. This will clear your saved choices 
                  and show the cookie consent banner again when you next visit.
                </p>
                <button
                  onClick={clearCookieConsent}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Clear Cookie Preferences
                </button>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">📱 Third-Party Cookies</h3>
                <p className="text-gray-700 text-sm">
                  SANIBIZ MIS does not use third-party advertising cookies. We only use first-party cookies that are 
                  necessary for our service to function or to improve your experience.
                </p>
              </div>
            </div>
          </div>

          {/* Cookie Duration */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How Long Do Cookies Last?</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-4 bg-blue-50 p-4 rounded-lg">
                <span className="text-2xl">⏱️</span>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Session Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Temporary cookies that expire when you close your browser. Used for session management and navigation.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-green-50 p-4 rounded-lg">
                <span className="text-2xl">📅</span>
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">Persistent Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Remain on your device for a set period (e.g., JWT tokens last 7 days). Used for authentication and preferences.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 shadow-xl mb-8">
            <h2 className="text-2xl font-bold mb-4">Questions About Cookies?</h2>
            <p className="mb-6">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="space-y-3">
              <a href="mailto:info@starsolutions.biz" className="flex items-center space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
                <span className="text-xl">📧</span>
                <span className="font-medium">info@starsolutions.biz</span>
              </a>
              <a href="tel:+918133047257" className="flex items-center space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
                <span className="text-xl">📱</span>
                <span className="font-medium">+91 8133047257</span>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <Link
              to="/"
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Home</span>
            </Link>
            <Link
              to="/privacy-policy"
              className="flex items-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <span>Privacy Policy</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
